import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CellRenderer, CellRendererParams } from '../../core/interfaces/cell-renderer.interface';
import { UserRouteDisplayComponent } from 'app/shared/components/user-route-display/user-route-display.component';
import { getCode } from 'app/utils/method';

/**
 * User Route Cell Renderer Component
 *
 * Reusable cell renderer for displaying combined route and user information.
 * Uses UserRouteDisplayComponent internally to show route code, name, and assigned user.
 *
 * Usage in ColDef:
 * ```typescript
 * {
 *   field: 'subRoute',
 *   headerName: 'Ruta / Usuario',
 *   cellRenderer: UserRouteCellRendererComponent
 * }
 * ```
 */
@Component({
  selector: 'app-user-route-cell-renderer',
  standalone: true,
  imports: [CommonModule, UserRouteDisplayComponent],
  template: `
    <app-user-route-display
      [routeData]="{
        routeName: routeName,
        externalKeys: {
          code: routeCode
        }
      }"
      [userData]="{
        name: userName,
        username: userName
      }"
      [displayMode]="'compact'"
      [showRouteCode]="true"
      [showUser]="true"
      [showResponsible]="false">
    </app-user-route-display>
  `,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserRouteCellRendererComponent implements CellRenderer<any, any> {
  /**
   * Parameters from the grid containing the cell value and row data
   */
  @Input() params!: CellRendererParams<any, any>;

  /**
   * Get route code using getCode utility
   */
  get routeCode(): string {
    return getCode(this.params?.data?.subRoute) ?? '-';
  }

  /**
   * Get route name
   */
  get routeName(): string {
    return this.params?.data?.subRoute?.name ?? '-';
  }

  /**
   * Get user name from various possible locations
   */
  get userName(): string {
    const data = this.params?.data;
    return data?.user?.user_login?.username ?? data?.user ?? '-';
  }
}
